import { Box, Theme, Tooltip, Typography, styled, Button, keyframes, css } from "@mui/material"
import { addMinutes } from "date-fns"
import { addWeeks, eachWeekOfInterval } from "date-fns/esm"
import { cloneElement, FC, useEffect, useRef, useState } from "react"
import { useCallback } from "react"
import { LineChartData, LineChartDataWithPairs, LineChartResponseDto, LineChartWithPairsResponseDto, TimeRangeType } from "@/gen/api/metrics"
import { useUserTimeFormat } from "@/utils/userTimeFormat"

export type RequiredNonnull<T> = {
  [P in keyof T]-?: NonNullable<T[P]>
}

export type LineChartDataEx = RequiredNonnull<LineChartData> & {
  color: string;
}

export type LineChartResponseDtoEx = RequiredNonnull<Omit<LineChartResponseDto, 'series'>> & {
  series: LineChartDataEx[]
}

export const perfThreshold = 1500

export const generalizeData = (theme: Theme, x: LineChartResponseDto | LineChartWithPairsResponseDto | undefined, colorsOverride?: string[]): LineChartResponseDtoEx => {
  if (!x) {
    return {
      legend: [] as string[],
      series: [] as LineChartDataEx[],
      xaxis: [] as string[],
    }
  }
  const colors = colorsOverride || [
    theme.palette.warning.dark,
    theme.palette.success.light,
    theme.palette.error.dark,
    theme.palette.primary.light,
    theme.palette.primary.dark,
    theme.palette.warning.light,
    theme.palette.success.dark,
    theme.palette.error.light,
  ]
  
  const legend = x.legend || []
  //@ts-ignore
  const xaxis = x.xaxis || []
  const series = (x.series || []).flatMap((s:LineChartData | LineChartDataWithPairs) => !s.name || !s.data ? [] : [{...s, color: ''} as LineChartDataEx]).map((s, i) => (s.color = colors[i], s))  

  return {
    legend,
    xaxis,
    series,
  }
}

export const samplingLineChartData = (x: LineChartResponseDtoEx, displayRange: TimeRangeType): LineChartResponseDtoEx => {
  switch (displayRange) {
    
    case TimeRangeType.Month: {
      // at most 1 data point per day
      const indexes = x.xaxis.reduceRight((acc, t, i) => {
        const day = (new Date(t)).toDateString()
        if (day !== acc.curr) {
          acc.curr = day
          acc.xaxis.push(i)
        }
        return acc
      }, { xaxis: [] as number[], curr: '1970-01-01' }).xaxis.reverse()
      const xaxis = indexes.map(i => x.xaxis[i])
      const series = x.series.map(s => ({...s, data: indexes.map(i => s.data[i])}))

      return {...x, xaxis, series};
    }
    
    case TimeRangeType.Year: {
      // at most 1 data point per week
      if (x.xaxis.length < 2) return x;

      const weekStarts = eachWeekOfInterval({ start: new Date(x.xaxis[0]), end: new Date(x.xaxis[x.xaxis.length - 1]) }).reverse()

      const indexes = weekStarts.reduce((acc, w) => {
        let t = acc.raw.pop()
        while (t && (new Date(t) > w)) {
          t = acc.raw.pop()
        }
        if (t && addWeeks(new Date(t), 1) > w) {
          acc.xaxix.push(acc.raw.length)
        }
        return acc
      }, { xaxix: [x.xaxis.length - 1] as number[], raw: [...x.xaxis] }).xaxix.reverse()

      const xaxis = indexes.map(i => x.xaxis[i])
      const series = x.series.map(s => ({...s, data: indexes.map(i => s.data[i])}))

      return {...x, xaxis, series};
    }

    case TimeRangeType.Week: 
    default:
      return x;
  }
}

export const genericChartOptions = (theme: Theme) => ({
  options: {
    chart: {
      background: 'transparent',
      stacked: false,
      toolbar: {
        show: false
      },
      zoom: { enabled: false }
    },
    dataLabels: {
      enabled: false
    },
    grid: {
      borderColor: theme.palette.divider,
      yaxis: {
        lines: {
          show: false
        }
      }
    },
    legend: {
      show: false
    },
    markers: {
      hover: {
        size: undefined,
        sizeOffset: 2
      },
      radius: 2,
      shape: 'circle' as const,
      size: 4,
      strokeWidth: 0
    },
    stroke: {
      curve: 'straight' as const,
      lineCap: 'butt' as const,
      width: 3
    },
    theme: {
      mode: theme.palette.mode
    },
    tooltip: {
      theme: theme.palette.mode
    },
    xaxis: {
      axisBorder: {
        color: theme.palette.divider
      },
      axisTicks: {
        color: theme.palette.divider,
        show: true
      },
      labels: {
        style: {
          colors: theme.palette.text.secondary
        }
      }
    },
    yaxis: [
      {
        title: {
          style: {
            fontWeight: '400',
          },
        },
        axisBorder: {
          color: theme.palette.divider,
          show: true
        },
        axisTicks: {
          color: theme.palette.divider,
          show: true
        },
        labels: {
          style: {
            colors: theme.palette.text.secondary
          }
        }
      },
    ]
  },
});


// optimize legends display

interface LegendInfo1 {
  schema: string;
  tableName: string;
}

interface LegendInfo2 extends LegendInfo1 {
  indexName: string;
}

const parseLegendStr1 = (x: string): LegendInfo1 | undefined => {
  const rgx = /([^.]*)\.([^.]*)/
  if (!rgx.test(x)) return undefined;

  const m = rgx.exec(x)
  if (!m || m.length < 3) return undefined;

  return { schema: m[1], tableName: m[2] }
}

const parseLegendStr2 = (x: string): LegendInfo2 | undefined => {
  const rgx = /\(([^.]*)\.([^.]*)\)(.*)/
  if (!rgx.test(x)) return undefined;

  const m = rgx.exec(x)
  if (!m || m.length < 4) return undefined;

  return { schema: m[1], tableName: m[2], indexName: m[3] }
}

type TruncChoice = number | 'full' | 'abstain'

const decideTrucateOption = (inputArr: string[]): TruncChoice[] => {
  const decision = 
    inputArr.reduce((acc, s) => (acc[s] = 'full', acc), {} as Record<string, TruncChoice>)
  let acc = {
    maxRound: Math.max.apply(Math, inputArr.map(s => s.length)),
    currRound: 0,
    competitors: { ['-1' as string]: inputArr },
    decision: decision as Record<string, TruncChoice>,
  }
  const reducer = (x: typeof acc) => {
    if (x.currRound >= x.maxRound) return x

    const remainingCompetitors =
      Object.keys(x.competitors).reduce((nextRoundNominees, k, i) => {
        const bag = x.competitors[k].reduce((a, c) => {
          const char = c[x.currRound]
          if (char) {
            const queue = a[char] || (a[char] = [])
            queue.push(c)
          } else {
            x.decision[c] = 'full'
          }
    
          return a
        }, {} as Record<string, string[]>)
    
        for(const k1 in bag) {
          if (bag[k1].length === 1) {
            const c = bag[k1][0]
            // possible extension point 1
            x.decision[c] = c.length > x.currRound + 2 ? (x.currRound + 2) : 'full'
          } else {
            nextRoundNominees[i + k1] = bag[k1]
          }
        }
        
        return nextRoundNominees
      }, {} as Record<string, string[]>)

    return {
      ...x,
      currRound: x.currRound + 1,
      competitors: remainingCompetitors,
    }
  }

  while( 
    Object.keys(acc.competitors).length > 0 
    && acc.currRound < acc.maxRound 
  ) {
    acc = reducer(acc)
  }

  // possible extension point 2
  // final judge decision

  return inputArr.map(c => { 
    const defaultTruncLength = Math.max(acc.currRound + 1, 12)
    const d = acc.decision[c]
    if (d === 'abstain') {
      return defaultTruncLength <= c.length ? defaultTruncLength : 'full'
    } else if (d === 'full') {
      return 'full'
    } else {
      return Math.max(d, 12)
    }
  })
}

const truncWithChoice = (x:string, c: TruncChoice) => {
  if (c === 'full' || c === 'abstain') {
    return x
  } else {
    return x.substr(0, c) + (x.length > c ? '..' : '')
  }
}


interface ChartLegendsProps {
  series: LineChartDataEx[],
  onClick: (x: LineChartDataEx, checked: boolean) => void,
}

const ShortLegendText = styled(Box)(({ theme }) => ({
  '& .schema': {
    color: theme.palette.text.secondary,
  },
  '& .tablename': {
    color: theme.palette.text.primary,
  },
  '& .indexname': {
    color: theme.palette.text.primary,
  },
}))

export const ChartLegends: FC<ChartLegendsProps> = (props) => {
  const { series } = props
  const legendInfoArr = series.map(s => parseLegendStr1(s.name))
  const a1: LegendInfo1[] = legendInfoArr.filter(i => !!i) as any
  const allSameSchema = [...new Set(a1.map(x => x.schema))].length <= 1
  
  const displayText = (x: LegendInfo1, full?: boolean) => (
    <>
      {(!allSameSchema || full) && <Typography className="schema" variant="caption">{x.schema}.</Typography>}
      <Typography component="span" className="tablename" variant="subtitle2">{x.tableName}</Typography>
    </>
  )
  const LegendPiece = ({ i }: {i:number}) => {
    const item = legendInfoArr[i]
    if (!item) {
      return (
        <Typography color="textPrimary" variant="subtitle2">{series[i].name}</Typography>
      )
    }
    return (
      <Tooltip
        title={
          <Box>
            {displayText(item, true)}
          </Box>
        }
      >
        <ShortLegendText>
          {displayText(item)}
        </ShortLegendText> 
      </Tooltip>
    )
  }
  
  return (
    <ChartLegendsCommon {...props}>
      <LegendPiece i={0} />
    </ChartLegendsCommon>
  )
}

export const ChartLegendsCommon: FC<ChartLegendsProps> = ({ 
  series,
  onClick,
  children,
}) => {
  const checkedStateRef = useRef(series.reduce((acc, s) => {
    acc[s.name] = true
    return acc
  }, {} as Record<string, boolean>))

  useEffect(() => {
    checkedStateRef.current = series.reduce((acc, s) => {
      acc[s.name] = true
      return acc
    }, {} as Record<string, boolean>)
    setInnerVer(new Date())
  }, [series])

  const [, setInnerVer] = useState(new Date())

  const handleClick = useCallback((x:LineChartDataEx) => {
    const checked = !checkedStateRef.current[x.name]
    checkedStateRef.current[x.name] = checked
    onClick(x, checked)
    setInnerVer(new Date())
  }, [])

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap'
      }}
    >
      {series.map((item, i) => (
        <Button
          component={Box}
          key={item.name}
          sx={{
            alignItems: 'center',
            display: 'flex',
            mr: 2,
            '& .MuiButton-label .MuiTypography-root': {
              color: !checkedStateRef.current[item.name] ? 'action.disabled' : '',
            },
          }}
          size="small"
          onClick={() => handleClick(item)}
        >
          <Box
            sx={{
              backgroundColor: item.color,
              borderRadius: '50%',
              height: 8,
              ml: 1,
              mr: 1,
              width: 8
            }}
          />
          {
            (children && cloneElement(children as any, { i }))
          }
        </Button>
      ))}
    </Box>
  )
}


export const ChartLegends2: FC<ChartLegendsProps> = (props) => {
  const { series } = props
  const legendInfoArr = series.map(s => parseLegendStr2(s.name))
  const a1: LegendInfo1[] = legendInfoArr.filter(i => !!i) as any
  const allSameSchema = [...new Set(a1.map(x => x.schema))].length <= 1
  
  const truncOptions = decideTrucateOption(
    legendInfoArr.map(s => s?.indexName || '')
  )
  const displayText = (x: LegendInfo2, full?: boolean) => (
    <>
      {full &&
        <>
          <Typography component="span" variant="body2">(</Typography>
          {(!allSameSchema || full) && <Typography className="schema" variant="caption">{x.schema}.</Typography>} 
          <Typography component="span" className="tablename" variant="subtitle2">{x.tableName}</Typography>
          <Typography component="span" variant="body2">)</Typography>
        </>
      }
      <Typography component="span" className="indexname" variant={full ? "body2" : "subtitle2"}>{" "}{x.indexName}</Typography>
    </>
  )
  const LegendPiece = ({ i }: {i:number}) => {
    const item = legendInfoArr[i]
    if (!item) {
      return (
        <Typography color="textPrimary" variant="body2">{series[i].name}</Typography>
      )
    }
    return (
      <Tooltip
        title={
          <Box>
            {displayText(item, true)}
          </Box>
        }
      >
        <ShortLegendText>
          {displayText({ 
            ...item, 
            indexName: truncWithChoice(item.indexName, truncOptions[i])
          })}
        </ShortLegendText>
      </Tooltip>
    )
  }
  
  return (
    <ChartLegendsCommon {...props}>
      <LegendPiece i={0} />
    </ChartLegendsCommon>
  )
}

export const ChartNoData: FC<{show:boolean}> = ({show}) => {
  return (
    !show ? null :
    <Typography
      color="error"
      variant="h6"
      sx={{
        position: 'absolute',
        top: 'calc(50% - 28px)',
        left: 'calc(50% - 36px)',
        px: 0.5,
        bgcolor: 'text.disabled',
        opacity: 0.8,
      }}
    >
      No Data
    </Typography>
  )
}


const ChartLoadingBox = styled(Box)(({theme}) => {
  const waveKeyframe = keyframes`
    50% { 
      transform: scaleY(1.5);
      background-color: ${theme.palette.success.light};
      opacity: 1;
    }
  `
  return ({
    position: 'absolute',
    top: 'calc(50% - 40px)',
    left: 'calc(50% - 30px)',
    width: 60,
    height: 30,
    '& > div': {
      ...(css`
      animation: ${waveKeyframe} 1.5s ease-in-out infinite;
      `),
    },
    '& div': {
      float: 'left',
      backgroundColor: theme.palette.success.main,
      height: '100%',
      width: 5,
      marginRight: '1px',
      display: 'inline-block',
      opacity: 0,
      transform: 'scaleY(0.1)',
    },
    '& .shaft1': { animationDelay: '0.05s' },
    '& .shaft2': { animationDelay: '0.1s' },
    '& .shaft3': { animationDelay: '0.15s' },
    '& .shaft4': { animationDelay: '0.2s' },
    '& .shaft5': { animationDelay: '0.25s' },
    '& .shaft6': { animationDelay: '0.3s' },
    '& .shaft7': { animationDelay: '0.35s' },
    '& .shaft8': { animationDelay: '0.4s' },
    '& .shaft9': { animationDelay: '0.45s' },
    '& .shaft10': { animationDelay: '0.5s' },
  })
})

export const ChartLoading: FC<{show?:boolean}> = ({show}) => {
  return (
    !show ? null :
    <ChartLoadingBox >
      <div className="shaft1"></div>
      <div className="shaft2"></div>
      <div className="shaft3"></div>
      <div className="shaft4"></div>
      <div className="shaft5"></div>
      <div className="shaft6"></div>
      <div className="shaft7"></div>
      <div className="shaft8"></div>
      <div className="shaft9"></div>
      <div className="shaft10"></div>
    </ChartLoadingBox>
  )
}

export const useChartUtilsWithUserTime = () => {
  const format = useUserTimeFormat()

  const timestampWithUserTime = (t:any) => {
    const t0 = new Date(t)
    const t1 = addMinutes(t0, -t0.getTimezoneOffset())
    return Number(format(t1, 'T'))
  }
  const pairWithUserTime = (pair: any[]) => [timestampWithUserTime(pair[0]), pair[1]]
  
  return {
    timestampWithUserTime,
    pairWithUserTime,
  }
}
